import React, { useContext, Fragment, useMemo, PropsWithChildren } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import PageWrapper from "../../../../../components/PageWrapper/PageWrapper";
import { dataStore } from "../../../../../contexts/DataContext";
import TextChip from "../../../../../design-system-components/TextChip/TextChip";
import { getModuleById } from "../../../../../utils/dataRetrieval";
import PageHeader from "../../../../../components/PageHeader/PageHeader";
import {
    formatClassAcronym,
    getDuoName,
    isCompleted,
    isPlayable,
} from "../../utils";
import DuoCard from "../../../../../components/DuoCard/DuoCard";
import { Item, useOverlayTriggerState } from "react-stately";
import AddDuoModal from "./AddDuoModal/AddDuoModal";
import useTeacherDuo from "../useTeacherDuo";
import Tabs from "../../../../../design-system-components/Tabs/Tabs";
import { DuoSession } from "../../../../../interfaces/Session";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { pageTitles } from "../../../../../utils/messages";
import { BaseRouteWithHeaderWrapper } from "../../../../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import { resourceTitle } from "../../../../../utils/format";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";

import "./DuoWorkModeItem.scss";

const DuoWorkModeItem = () => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const { resourceId } = useParams<{ resourceId: string }>();

    const addDuoModal = useOverlayTriggerState({});

    return (
        <Wrapper resourceId={resourceId}>
            <Tabs className="duo-wm-item" variant="accent">
                <Item
                    title={intl.formatMessage(messages.inProgress)}
                    key="in-progress"
                >
                    {isOnline && (
                        <Button
                            label={intl.formatMessage(messages.addDuo)}
                            icons={[
                                {
                                    placement: "left",
                                    path: "add",
                                },
                            ]}
                            variant="secondary"
                            className="duo-wm-item__add-duo-button"
                            onPress={() => {
                                addDuoModal.open();
                            }}
                        />
                    )}
                    <DuoList moduleId={resourceId} filter={isPlayable} />
                </Item>

                <Item
                    title={intl.formatMessage(messages.finished)}
                    key="finished"
                >
                    <DuoList moduleId={resourceId} filter={isCompleted} />
                </Item>
            </Tabs>

            {addDuoModal.isOpen && <AddDuoModal state={addDuoModal} />}
        </Wrapper>
    );
};

const Wrapper = ({
    resourceId,
    children,
}: PropsWithChildren<{ resourceId: string }>) => {
    const intl = useIntl();
    const { data } = useContext(dataStore);
    const currentItem = getModuleById(resourceId, data);
    return (
        <BaseRouteWithHeaderWrapper
            title={intl.formatMessage(pageTitles.teacherModuleDuos, {
                item: resourceTitle(
                    {
                        module: {
                            id: resourceId,
                            numbering: true,
                            short: true,
                        },
                    },
                    data,
                    intl
                ).module!,
            })}
        >
            <PageWrapper>
                <PageHeader
                    title={{
                        content:
                            currentItem.title.short ?? currentItem.title.long,
                    }}
                />
                {children}
            </PageWrapper>
        </BaseRouteWithHeaderWrapper>
    );
};
type DuoListProps = {
    moduleId: string;
    filter?: (duo: DuoSession) => boolean;
};
const DuoList = ({ moduleId, filter = () => true }: DuoListProps) => {
    const intl = useIntl();
    const { getGroups } = useTeacherDuo();

    const groups = useMemo(
        () =>
            getGroups(moduleId).filter(
                (group) =>
                    group.duos &&
                    group.duos.filter(
                        (duo) => duo.moduleId === moduleId && filter(duo)
                    ).length > 0
            ),
        [getGroups, moduleId, filter]
    );

    return (
        <div>
            {groups.map((group) => (
                <Fragment key={group.id}>
                    <div className="duo-wm-item__group-title">
                        <TextChip
                            content={formatClassAcronym(group.name)}
                            classNames="duo-wm-item__group-title__group-chip"
                        />
                        <h3>{group.name}</h3>
                    </div>
                    <ol>
                        {group.duos
                            ?.filter(
                                (duo) =>
                                    duo.moduleId === moduleId && filter(duo)
                            )
                            .map((duo) => (
                                <li key={duo.id}>
                                    <DuoCard
                                        duoId={duo.id}
                                        name={{
                                            as: "h3",
                                            content:
                                                duo.name ||
                                                getDuoName(duo, intl),
                                        }}
                                        // COULD: A décommenter si on implémente la progression des exercices dans un item duo
                                        // progression={{
                                        //     value: duo.exercisesDone
                                        //         .length,
                                        //     maxValue:
                                        //         duo.totalExercises,
                                        //     label: intl.formatMessage(
                                        //         messages.duoCardProgressionLabel
                                        //     ),
                                        //     valueLabel: `${intl.formatMessage(
                                        //         messages.duoCardProgressionValue,
                                        //         {
                                        //             nbOfExercisesDone:
                                        //                 duo
                                        //                     .exercisesDone
                                        //                     .length,
                                        //         }
                                        //     )} ${duo.totalExercises}`,
                                        //     colors: {
                                        //         foreground:
                                        //             "var(--mode-duo-primary-medium)",
                                        //         background:
                                        //             "var(--mode-duo-primary-light)",
                                        //     },
                                        // }}
                                    />
                                </li>
                            ))}
                    </ol>
                </Fragment>
            ))}
        </div>
    );
};

const messages = defineMessages({
    inProgress: {
        id: "d-mode-in-progress-label",
        defaultMessage: "In progress",
    },
    finished: {
        id: "duo-mode-finished-label",
        defaultMessage: "Finished",
    },
    addDuo: {
        id: "duo-mode-add-button-label",
        defaultMessage: "Add a duo",
    },
    duoCardProgressionLabel: {
        id: "duo-mode-duo-card-progression-label",
        defaultMessage: "Progression",
    },
    duoCardProgressionValue: {
        id: "duo-mode-duo-card-progression-value",
        defaultMessage: `{nbOfExercisesDone, plural,
                            =0 {0 exercise out of}
                            =1 {1 exercise out of}
                            other {# exercises out of}
                        }`,
    },
});

export default DuoWorkModeItem;
